import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Shield, AlertTriangle, ShieldAlert, Info, CheckCircle, CheckCircle2,
  RefreshCw, Clock, XCircle, Camera, Upload, CalendarDays, MessageSquare
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEV = {
  critical: { label: "Critical", color: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  high:     { label: "High",     color: "bg-orange-50 text-orange-700 border-orange-200", icon: ShieldAlert },
  medium:   { label: "Medium",   color: "bg-amber-50 text-amber-700 border-amber-200", icon: Info },
  low:      { label: "Low",      color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
};

const STATUS = {
  issued:               { label: "Awaiting Your Response", color: "bg-slate-100 text-slate-600" },
  in_progress:          { label: "In Progress",            color: "bg-blue-50 text-blue-700" },
  completed_by_insured: { label: "Submitted — Under Review", color: "bg-amber-50 text-amber-700" },
  disputed:             { label: "Disputed",               color: "bg-red-50 text-red-700" },
  closed:               { label: "Closed",                 color: "bg-green-50 text-green-700" },
};

function RecCard({ rec, token, onUpdated }) {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const sev = SEV[rec.severity] || SEV.medium;
  const st = STATUS[rec.status] || STATUS.issued;
  const Icon = sev.icon;
  const closed = rec.status === "closed";
  const overdue = !closed && rec.status !== "completed_by_insured" && rec.due_date && rec.due_date < new Date().toISOString();

  const respond = async (type) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/recs/portal/${token}/${rec.id}/respond`,
        { response_type: type, note, target_date: targetDate || null });
      onUpdated(res.data);
      toast.success("Response submitted — thank you");
      setMode(null); setNote(""); setTargetDate("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit response");
    }
    setSaving(false);
  };

  const uploadProof = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await axios.post(`${API}/recs/portal/${token}/${rec.id}/proof-photo`, fd);
        onUpdated({ ...rec, proof_photos: [...(rec.proof_photos || []), res.data.url] });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Photo upload failed");
      }
    }
    setUploading(false);
  };

  return (
    <div className={`bg-white border rounded-2xl p-5 shadow-sm ${overdue ? "border-red-200" : "border-slate-200"}`}
      data-testid={`rec-card-${rec.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${sev.color}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-800">{rec.title}</p>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.color}`} data-testid={`rec-status-${rec.id}`}>{st.label}</span>
            {overdue && <span className="text-xs font-bold text-red-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Overdue</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1.5">{rec.description}</p>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs font-semibold text-blue-700 mb-1">Required Action</p>
            <p className="text-sm text-blue-800">{rec.recommendation}</p>
          </div>
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" />
            Respond by {rec.due_date ? new Date(rec.due_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}
          </p>

          {rec.response && (
            <div className="mt-3 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <p className="font-semibold text-slate-600 mb-0.5">Your response ({new Date(rec.response.responded_at).toLocaleDateString()})</p>
              {rec.response.note && <p className="italic">&ldquo;{rec.response.note}&rdquo;</p>}
              {rec.response.target_date && <p className="mt-1">Target completion: {rec.response.target_date}</p>}
            </div>
          )}

          {(rec.proof_photos || []).length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {rec.proof_photos.map((u, i) => (
                <img key={i} src={`${BACKEND_URL}${u}`} alt="Proof" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
              ))}
            </div>
          )}

          {!closed && rec.status !== "completed_by_insured" && (
            <div className="mt-4">
              {!mode ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid={`rec-completed-btn-${rec.id}`} onClick={() => setMode("completed")}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                    <CheckCircle2 className="w-4 h-4" /> Mark Completed
                  </button>
                  <button data-testid={`rec-inprogress-btn-${rec.id}`} onClick={() => setMode("in_progress")}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-600 hover:text-white transition-colors">
                    <RefreshCw className="w-4 h-4" /> In Progress
                  </button>
                  <button data-testid={`rec-dispute-btn-${rec.id}`} onClick={() => setMode("disputed")}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-600 hover:text-white transition-colors">
                    <XCircle className="w-4 h-4" /> Dispute
                  </button>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50" data-testid={`rec-respond-form-${rec.id}`}>
                  <p className="text-sm font-semibold text-slate-700">
                    {mode === "completed" ? "Confirm completion" : mode === "in_progress" ? "Tell us your plan" : "Explain the dispute"}
                  </p>
                  <textarea
                    data-testid={`rec-note-input-${rec.id}`}
                    value={note} onChange={e => setNote(e.target.value)} rows={2}
                    placeholder={mode === "completed" ? "What was done? (optional)" : mode === "in_progress" ? "What's the plan? (optional)" : "Why do you believe this doesn't apply? (required)"}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-navy resize-none bg-white"
                  />
                  {mode === "in_progress" && (
                    <input type="date" data-testid={`rec-target-date-${rec.id}`} value={targetDate} onChange={e => setTargetDate(e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-navy" />
                  )}
                  {mode === "completed" && (
                    <>
                      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                        data-testid={`rec-proof-input-${rec.id}`}
                        onChange={e => { uploadProof(e.target.files); e.target.value = ""; }} />
                      <button onClick={() => fileRef.current?.click()} disabled={uploading}
                        data-testid={`rec-upload-proof-btn-${rec.id}`}
                        className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-green-600 hover:text-green-700 transition-colors">
                        {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        Add proof photo (recommended)
                      </button>
                    </>
                  )}
                  <div className="flex items-center gap-2">
                    <button data-testid={`rec-submit-response-btn-${rec.id}`} disabled={saving} onClick={() => respond(mode)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1B2B5E] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Submit
                    </button>
                    <button onClick={() => setMode(null)} className="text-sm text-slate-400 hover:text-slate-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RecPortal() {
  const { recToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recs/portal/${recToken}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Portal not found");
    }
    setLoading(false);
  }, [recToken]);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (updated) => {
    setData(d => ({ ...d, recommendations: d.recommendations.map(r => (r.id === updated.id ? updated : r)) }));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><RefreshCw className="w-8 h-8 text-[#1B2B5E] animate-spin" /></div>;
  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3">
      <AlertTriangle className="w-10 h-10 text-red-400" />
      <p className="text-slate-700 font-medium">{error}</p>
    </div>
  );

  const open = data.recommendations.filter(r => !["closed"].includes(r.status));
  const done = data.recommendations.filter(r => r.status === "closed");
  const respondedCount = data.recommendations.filter(r => r.status !== "issued").length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rec-portal">
      <Toaster richColors position="bottom-center" />
      <header className="bg-[#1B2B5E] px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-widest mb-2">
            <Shield className="w-4 h-4" /> {data.carrier_name} — Corrective Recommendations
          </div>
          <h1 className="text-2xl font-bold text-white">{data.insured_name}</h1>
          <p className="text-white/70 text-sm mt-1">{data.property_address}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center justify-between" data-testid="rec-progress-card">
          <div>
            <p className="font-semibold text-slate-800">Your response progress</p>
            <p className="text-xs text-slate-400 mt-0.5">Respond to each item below — mark it complete, in progress, or dispute it</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#1B2B5E]" data-testid="rec-progress-count">{respondedCount}/{data.recommendations.length}</p>
            <p className="text-xs text-slate-400">responded</p>
          </div>
        </div>

        {open.map(r => <RecCard key={r.id} rec={r} token={recToken} onUpdated={onUpdated} />)}

        {done.length > 0 && (
          <>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider pt-2">Closed by your carrier</p>
            {done.map(r => <RecCard key={r.id} rec={r} token={recToken} onUpdated={onUpdated} />)}
          </>
        )}

        {data.recommendations.length === 0 && (
          <div className="text-center py-16">
            <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">No recommendations on file</p>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 pt-4 pb-8">Powered by RiskSurvey.ai — questions? Contact your insurance representative.</p>
      </main>
    </div>
  );
}
